import React, { useContext, useEffect, useState } from "react";
import { RoutesContext } from "../../routes/router";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Divider, Dropdown, Flex, Layout, Menu, Tooltip, Typography } from "antd";
import XdLogo from "../../assets/XdLogo.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faGear,
  faQuestion,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { DashboardIcon } from "../../assets/icons/DashboardIcon";
import { OrdersIcon } from "../../assets/icons/OrdersIcon";
// import { LeadsIcon } from "../../assets/icons/LeadsIcon";
import { TermsIcon } from "../../assets/icons/TermsIcon";
import styles from "./main-layout.module.scss";
import { AuthContext } from "../../context/AuthProvider";
import { authService } from "../../services";

export const MainLayout = ({
  children,
  subNavPanel = null,
}: {
  children: React.ReactNode;
  subNavPanel?: React.ReactNode;
}) => {
  const {
    user,
    logout,
    switcherAccounts,
    selectedSwitcherAccountId,
    selectSwitcherAccount,
  } = useContext(AuthContext);

  const linkableRoutes = React.useContext(RoutesContext);
  const location = useLocation();

  // eslint-disable-next-line
  const navigate = React.useCallback(
    (
      (routerNavigate: NavigateFunction) =>
      (path: string, replace = false) => {
        if (!path.includes(":")) {
          routerNavigate(path, { replace });
          return true;
        }
        return false;
      }
    )(useNavigate()),
    [],
  );

  const [selectedKeys, setSelectedKeys] = useState([location.pathname]);
  const menuItems = [
    {
      key: "/Dashboard",
      icon: <span style={{ display: "inline-flex", alignItems: "center" }}><DashboardIcon /></span>,
      label: "Dashboard",
      onClick: () => {
        navigate("/Dashboard");
      },
    },
    {
      key: "/Orders",
      icon: <span style={{ display: "inline-flex", alignItems: "center" }}><OrdersIcon /></span>,
      label: "Orders",
      onClick: () => {
        navigate("/Orders");
      },
    },
    // {
    //   key: "/Leads",
    //   icon: <span style={{ display: "inline-flex", alignItems: "center" }}><LeadsIcon /></span>,
    //   label: "Leads",
    //   onClick: () => {
    //     navigate("/");
    //   },
    // },
  ];

  const bottomItems = [
    {
      key: "/Settings",
      icon: <FontAwesomeIcon icon={faGear} style={{color: '#99A0A7'}}/>,
      label: "Settings",
      onClick: () => {
        navigate("/Settings");
      },
    },
    {
      key: "/Help",
      icon: <FontAwesomeIcon icon={faQuestion} style={{color: '#99A0A7'}}/>,
      label: "Help",
      onClick: () => {
        navigate("/Help");
      },
    },
    {
      key: "/Terms",
      icon: <span style={{ display: "inline-flex", alignItems: "center", color: '#99A0A7' }}><TermsIcon /></span>,
      label: "Terms and Conditions",
      onClick: () => {
        navigate("/Terms");
      },
    },
  ];

  useEffect(() => {
    setSelectedKeys([location.pathname]);
  }, [location]);

  return (
    <Layout className={styles.mainLayout}>
      <Layout.Sider
        className={styles.nav}
        theme="light"
        width={250}
        trigger={null}
      >
        <Flex justify="space-between" vertical style={{ height: "100%" }}>
          <div className={styles.navHeader} onClick={(e) => navigate("/")}>
            <span className={styles.xdLogo}>
              <img
                src={XdLogo}
                alt="icon"
              />
            </span>
          </div>
          <Menu
            className={styles.menu}
            mode="inline"
            inlineIndent={12}
            selectedKeys={selectedKeys}
            defaultSelectedKeys={["home"]}
            items={menuItems}
            onClick={(e) => {
              if (!e.key.includes("|")) return;
              const [group, key] = e.key.split("|");
              const route = linkableRoutes.find(
                (r) => r.parent === group && r.title === key,
              );
              if (!route) return;
              navigate(route.path as any);
            }}
          />

          <div className="bottom">
            <Menu
              className={`${styles.menu} ${styles.subMenu}`}
              mode="inline"
              inlineIndent={12}
              selectedKeys={selectedKeys}
              defaultSelectedKeys={["home"]}
              items={bottomItems}
              onClick={(e) => {
                if (!e.key.includes("|")) return;
                const [group, key] = e.key.split("|");
                const route = linkableRoutes.find(
                  (r) => r.parent === group && r.title === key,
                );
                if (!route) return;
                navigate(route.path as any);
              }}
            />
            {switcherAccounts.length > 0 && (
              <Dropdown
                trigger={["click"]}
                menu={{
                  selectedKeys: selectedSwitcherAccountId
                    ? [selectedSwitcherAccountId]
                    : [],
                  items: switcherAccounts.map((account) => ({
                    key: account.id,
                    label: account.name,
                  })),
                  onClick: ({ key }) => {
                    if (key !== selectedSwitcherAccountId) {
                      selectSwitcherAccount(key);
                    }
                  },
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    padding: "8px 12px",
                    marginTop: 8,
                  }}
                >
                  <Typography.Text ellipsis style={{ fontWeight: 500 }}>
                    {switcherAccounts.find(
                      (a) => a.id === selectedSwitcherAccountId,
                    )?.name ?? switcherAccounts[0]?.name}
                  </Typography.Text>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    style={{ color: "#99A0A7" }}
                  />
                </div>
              </Dropdown>
            )}
            <Divider style={{ marginLeft: -10, marginRight: -10, width: "auto", marginTop: 10, marginBottom: 20, color: '#EBECED' }} />
            <div style={{ display: "flex", alignItems: "center", gap: 8, width: "auto" }}>
              <Avatar
                size={48}
                style={{ backgroundColor: "#334150", flexShrink: 0 }}
              >{`${user?.firstName?.[0]}${user?.lastName?.[0]}`}</Avatar>
              <div style={{ textAlign: "left", flex: 1, minWidth: 0 }}>
                <Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingBottom: 4
                    }}
                  >
                    <strong>{`${user?.firstName} ${user?.lastName}`}</strong>
                    <Tooltip title="Logout">
                      <FontAwesomeIcon
                        icon={faRightFromBracket}
                        className={styles.logoutIcon}
                        onClick={async () => {
                          try {
                            await authService.logout();
                          } finally {
                            logout();
                            navigate("/Login");
                          }
                        }}
                      />
                    </Tooltip>
                  </div>
                  {user?.email}
                </Typography>
              </div>
            </div>
          </div>
        </Flex>
      </Layout.Sider>
      <Layout.Content style={{ height: "100%" }}>
        <div style={{ height: "100%" }}>{children}</div>
      </Layout.Content>
    </Layout>
  );
};
